/**
 * Popup orchestration for the server-side Microsoft Graph authorization step.
 *
 * The browser never handles provider tokens: it only opens a popup, points it
 * at the server-validated Microsoft authorization URL, and waits for the API
 * callback landing (`/?oauth=connected` or `/?oauth=failed&reason=...`) to
 * signal the opener. No provider credential, token, or content ever transits
 * this channel — only a typed outcome and a correlation nonce.
 *
 * The callback landing is served with a COOP that forces a browsing-context-group
 * swap when the popup returns from the cross-origin provider, which clears both
 * `window.opener` and `window.name`. So the opener and popup rendezvous over a
 * same-origin BroadcastChannel — unaffected by the swap — where the opener
 * announces a per-authorization nonce and the popup echoes a nonce-tagged
 * result. A surviving `window.opener` (e.g. a callback served without the swap)
 * still works through the direct `postMessage` path in parallel.
 */

const POPUP_NAME = "obsina-microsoft-oauth";
const MESSAGE_SOURCE = "obsina-oauth-callback";
/** Same-origin channel that survives the COOP browsing-context-group swap. */
const BROADCAST_CHANNEL_NAME = "obsina-oauth-callback";
/** Separates the stable popup name from its per-authorization correlation id. */
const NONCE_SEPARATOR = ":";

const POPUP_WIDTH = 520;
const POPUP_HEIGHT = 640;

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000;
const CLOSE_POLL_INTERVAL_MS = 400;
/** Grace window so a queued success/failure message wins over a closed popup. */
const CLOSE_GRACE_MS = 1_000;
/** Small delay before the popup closes itself so the opener can read the event. */
const SELF_CLOSE_DELAY_MS = 200;
/** How often the opener re-announces that it is waiting for its popup. */
const AWAIT_INTERVAL_MS = 250;
/**
 * How long a severed popup listens for an awaiting opener before concluding it
 * is a redirected main tab (the popup-blocked fallback) rather than a popup.
 */
const OPENER_DISCOVERY_TIMEOUT_MS = 800;

export type OAuthOutcome =
  | { readonly status: "connected" }
  | { readonly status: "failed"; readonly reason: string | null };

export type OAuthPopupResult =
  | { readonly status: "connected" }
  | { readonly status: "failed"; readonly reason: string | null }
  | { readonly status: "cancelled" }
  | { readonly status: "timeout" };

export type MicrosoftAuthorizationOutcome =
  | { readonly kind: "connected" }
  | { readonly kind: "failed" }
  | { readonly kind: "cancelled" }
  | { readonly kind: "redirected" };

/**
 * Reads the API callback outcome from a landing URL search string. Only the two
 * documented server contracts are accepted; anything else yields `null`.
 */
export function readOAuthOutcomeFromLocation(
  search: string,
): OAuthOutcome | null {
  const params = new URLSearchParams(search);
  const oauth = params.get("oauth");
  if (oauth === "connected") return { status: "connected" };
  if (oauth === "failed") {
    const reason = params.get("reason");
    return {
      status: "failed",
      reason: reason && /^[a-z_]{1,64}$/.test(reason) ? reason : null,
    };
  }
  return null;
}

/** Strictly validates an untrusted `postMessage` payload from the popup. */
export function parseOAuthSignal(data: unknown): OAuthOutcome | null {
  if (typeof data !== "object" || data === null) return null;
  const record = data as Record<string, unknown>;
  if (record.source !== MESSAGE_SOURCE) return null;
  if (record.status === "connected") return { status: "connected" };
  if (record.status === "failed") {
    const reason = record.reason;
    return {
      status: "failed",
      reason:
        typeof reason === "string" && /^[a-z_]{1,64}$/.test(reason)
          ? reason
          : null,
    };
  }
  return null;
}

interface CallbackChannel {
  postMessage(message: unknown): void;
  close(): void;
  addEventListener(
    type: "message",
    listener: (event: MessageEvent) => void,
  ): void;
  removeEventListener(
    type: "message",
    listener: (event: MessageEvent) => void,
  ): void;
}

type BroadcastCapableWindow = Window & {
  BroadcastChannel?: new (name: string) => CallbackChannel;
};

/**
 * Opens the same-origin OAuth broadcast channel. Unlike `window.opener`, a
 * BroadcastChannel reaches every same-origin document regardless of the
 * browsing-context-group swap a provider's COOP forces on the popup, so it is
 * the recovery path when the opener reference is severed. Returns `null` when
 * the runtime does not expose BroadcastChannel.
 */
function openCallbackChannel(win: Window): CallbackChannel | null {
  const Channel = (win as BroadcastCapableWindow).BroadcastChannel;
  if (typeof Channel !== "function") return null;
  try {
    return new Channel(BROADCAST_CHANNEL_NAME);
  } catch {
    return null;
  }
}

/** Builds the popup's window name, embedding the per-authorization nonce. */
export function popupWindowName(nonce: string): string {
  return `${POPUP_NAME}${NONCE_SEPARATOR}${nonce}`;
}

/**
 * Extracts the nonce a popup carries in its name. `window.name` persists across
 * cross-origin navigations, so it survives the round trip through the provider
 * even when `window.opener` does not.
 */
function nonceFromWindowName(name: string | undefined): string | null {
  if (typeof name !== "string" || !name.startsWith(POPUP_NAME)) return null;
  const rest = name.slice(POPUP_NAME.length);
  return rest.startsWith(NONCE_SEPARATOR)
    ? rest.slice(NONCE_SEPARATOR.length)
    : null;
}

function generateNonce(win: Window): string {
  const source = win.crypto ?? globalThis.crypto;
  if (source && typeof source.randomUUID === "function") {
    return source.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Whether the opener document currently holds focus.
 *
 * A COOP browsing-context-group swap makes a live popup's handle report
 * `closed`, so `closed` on its own cannot tell "the user closed the popup" apart
 * from "the popup was severed and is still on screen signing in". While the user
 * works in the popup this document is not focused; a closed handle *and* focus
 * back here is the honest cancellation signal. Runtimes without a document (test
 * doubles) fall back to `true`, leaving the closed handle its plain meaning.
 */
function openerHasFocus(host: Window): boolean {
  const doc: Document | undefined = (host as Partial<Window>).document;
  return typeof doc?.hasFocus === "function" ? doc.hasFocus() : true;
}

/**
 * Runs inside a confirmed OAuth popup landing: broadcasts the typed outcome to
 * the waiting opener (same-origin, so a COOP swap that severed `window.opener`
 * cannot block it), posts it to a surviving opener as well, and closes the
 * popup. The payload carries only the outcome plus a correlation nonce — never a
 * token, code, or query value.
 */
function announceOAuthResult(
  win: Window,
  outcome: OAuthOutcome | null,
  nonce: string | null,
): void {
  const message = {
    source: MESSAGE_SOURCE,
    kind: "result",
    status: outcome?.status ?? "failed",
    reason: outcome?.status === "failed" ? outcome.reason : null,
    ...(nonce !== null ? { nonce } : {}),
  };
  const channel = openCallbackChannel(win);
  try {
    channel?.postMessage(message);
  } catch {
    // A channel that cannot deliver is non-fatal; a surviving opener still
    // receives the postMessage below.
  } finally {
    channel?.close();
  }
  try {
    const opener = win.opener as Window | null;
    opener?.postMessage(message, win.location.origin);
  } catch {
    // A severed or cross-origin opener cannot receive the post; the broadcast
    // above is the recovery path.
  }
  win.setTimeout(() => {
    try {
      win.close();
    } catch {
      // If the browser refuses to close the popup, the closing page keeps its
      // accessible instruction to close it manually.
    }
  }, SELF_CLOSE_DELAY_MS);
}

/**
 * Listens on the same-origin channel for an opener announcing that it is waiting
 * for its popup, and resolves that opener's correlation nonce. Returns `null`
 * when no opener answers within the timeout — the landing is then a redirected
 * main tab, not a popup. A COOP swap clears `window.opener` and `window.name`,
 * so this broadcast handshake is the only reliable way to tell them apart.
 */
function discoverAwaitingOpener(
  win: Window,
  timeoutMs: number,
): Promise<string | null> {
  const channel = openCallbackChannel(win);
  if (!channel) return Promise.resolve(null);
  return new Promise<string | null>((resolve) => {
    let done = false;
    const settle = (nonce: string | null) => {
      if (done) return;
      done = true;
      channel.removeEventListener("message", onMessage);
      channel.close();
      clearTimeout(timer);
      resolve(nonce);
    };
    const onMessage = (event: MessageEvent) => {
      const data: unknown = event.data;
      if (
        isRecord(data) &&
        data.source === MESSAGE_SOURCE &&
        data.kind === "awaiting" &&
        typeof data.nonce === "string"
      ) {
        settle(data.nonce);
      }
    };
    const timer = setTimeout(() => settle(null), timeoutMs);
    channel.addEventListener("message", onMessage);
    // Prompt any waiting opener to (re)announce its nonce immediately. Set up
    // above before this, so a synchronous reply cannot clear an unset timer.
    try {
      channel.postMessage({ source: MESSAGE_SOURCE, kind: "landed" });
    } catch {
      // Non-fatal: the opener also announces on an interval.
    }
  });
}

export type OAuthPopupLandingRole = "popup" | "application";

/**
 * Resolves how an OAuth callback landing should be handled. Returns `"popup"`
 * when this window is the authorization popup — it has already signalled its
 * opener and is closing itself — or `"application"` when it is a main tab that
 * completed OAuth via a full-page redirect and should render the app.
 */
export async function resolveOAuthPopupLanding(
  win: Window,
  options: { timeoutMs?: number } = {},
): Promise<OAuthPopupLandingRole> {
  const outcome = readOAuthOutcomeFromLocation(win.location.search);
  if (outcome === null) return "application";
  const opener = win.opener as Window | null;
  if (opener != null && opener !== win) {
    // The opener survived (callback served without a COOP swap): signal directly.
    announceOAuthResult(win, outcome, nonceFromWindowName(win.name));
    return "popup";
  }
  const nonce = await discoverAwaitingOpener(
    win,
    options.timeoutMs ?? OPENER_DISCOVERY_TIMEOUT_MS,
  );
  if (nonce === null) return "application";
  announceOAuthResult(win, outcome, nonce);
  return "popup";
}

/**
 * Opens a blank, centered popup synchronously (inside the click gesture so the
 * browser does not block it). Returns `null` when the popup is blocked, letting
 * the caller fall back to a full-page redirect.
 */
export function openMicrosoftAuthorizationPopup(
  win: Window = window,
  popupName: string = POPUP_NAME,
): Window | null {
  const left = win.screenX + Math.max(0, (win.outerWidth - POPUP_WIDTH) / 2);
  const top = win.screenY + Math.max(0, (win.outerHeight - POPUP_HEIGHT) / 2);
  const features = [
    "popup=yes",
    `width=${POPUP_WIDTH}`,
    `height=${POPUP_HEIGHT}`,
    `left=${Math.round(left)}`,
    `top=${Math.round(top)}`,
  ].join(",");
  try {
    return win.open("about:blank", popupName, features) ?? null;
  } catch {
    // A thrown `open` (blocked, or unimplemented in some runtimes) is treated
    // the same as a blocked popup so the caller can fall back to a redirect.
    return null;
  }
}

interface RunPopupOptions {
  readonly expectedOrigin: string;
  /** Correlation id scoping broadcast signals to this authorization run. */
  readonly nonce?: string;
  readonly host?: Window;
  readonly timeoutMs?: number;
  /**
   * Receives an outcome that arrives after the run already resolved as
   * cancelled. A cancellation is provisional because a severed handle reports
   * `closed`; this is how a late but real authorization still reaches the caller.
   */
  readonly onLateOutcome?: (outcome: OAuthOutcome) => void;
}

/**
 * Drives an already-open popup: navigates it to the server-provided
 * authorization URL, then resolves once the popup signals an outcome, is closed
 * by the user, or times out. Rejects only when the authorization URL cannot be
 * obtained.
 */
export function runMicrosoftAuthorizationPopup(
  popup: Window,
  loadAuthorizationUrl: () => Promise<string>,
  options: RunPopupOptions,
): Promise<OAuthPopupResult> {
  const host = options.host ?? window;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return new Promise<OAuthPopupResult>((resolve, reject) => {
    let settled = false;
    let closeGraceTimer: ReturnType<typeof setTimeout> | null = null;
    // A COOP swap makes a severed popup handle report `closed` while the popup is
    // still alive, so once it announces it landed on our origin we stop treating
    // `popup.closed` as a user cancellation and wait for its broadcast result.
    let landingSeen = false;
    const channel = openCallbackChannel(host);

    const stopCancellationWatch = () => {
      clearInterval(pollTimer);
      if (closeGraceTimer) clearTimeout(closeGraceTimer);
    };

    const cleanup = () => {
      host.removeEventListener("message", onMessage);
      channel?.removeEventListener("message", onBroadcast);
      channel?.close();
      stopCancellationWatch();
      clearInterval(awaitTimer);
      clearTimeout(timeoutTimer);
    };

    const finish = (result: OAuthPopupResult) => {
      if (settled) return;
      settled = true;
      stopCancellationWatch();
      if (!popup.closed) popup.close();
      resolve(result);
      // A cancellation is provisional: the handle of a COOP-severed popup reports
      // `closed` while the window is alive, so the run keeps announcing its nonce
      // and listening until the timeout. A late result then still reaches the
      // caller and lets the popup identify itself and close, instead of leaving
      // it stranded on the callback page with the opener never advancing.
      if (result.status !== "cancelled") cleanup();
    };

    /** Routes an outcome to the pending run, or to the late-outcome hook. */
    const deliver = (outcome: OAuthOutcome) => {
      if (!settled) {
        finish(outcome);
        return;
      }
      cleanup();
      options.onLateOutcome?.(outcome);
    };

    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      if (!popup.closed) popup.close();
      reject(
        error instanceof Error
          ? error
          : new Error("Microsoft authorization failed."),
      );
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== options.expectedOrigin) return;
      // When the popup is still identifiable it must be the one we opened; a
      // popup that already closed reports a null source, which the origin and
      // shape checks still cover.
      if (event.source !== null && event.source !== popup) return;
      const outcome = parseOAuthSignal(event.data);
      if (!outcome) return;
      deliver(outcome);
    };
    host.addEventListener("message", onMessage);

    // Recovery path when the provider's COOP severed `window.opener`, so the
    // popup's direct `postMessage` never reaches us. We announce our nonce on the
    // same-origin channel; the popup echoes a nonce-tagged result. The nonce
    // scopes the exchange to this run so an unrelated tab cannot resolve us.
    const announceAwaiting = () => {
      if (options.nonce === undefined) return;
      try {
        channel?.postMessage({
          source: MESSAGE_SOURCE,
          kind: "awaiting",
          nonce: options.nonce,
        });
      } catch {
        // Non-fatal: the popup also has the direct postMessage path.
      }
    };
    const onBroadcast = (event: MessageEvent) => {
      const data: unknown = event.data;
      if (isRecord(data) && data.kind === "landed") {
        landingSeen = true;
        if (closeGraceTimer) {
          clearTimeout(closeGraceTimer);
          closeGraceTimer = null;
        }
        announceAwaiting();
        return;
      }
      if (
        options.nonce !== undefined &&
        (!isRecord(data) || data.nonce !== options.nonce)
      ) {
        return;
      }
      const outcome = parseOAuthSignal(data);
      if (outcome) deliver(outcome);
    };
    channel?.addEventListener("message", onBroadcast);

    const pollTimer = setInterval(() => {
      if (settled || !popup.closed || closeGraceTimer || landingSeen) return;
      // The user is still signing in whenever the popup, not this document, has
      // focus — so a closed handle there is a COOP severance, never a
      // cancellation. Waiting is what keeps this run announcing long enough for
      // the popup to find it after a slow provider login.
      if (!openerHasFocus(host)) return;
      closeGraceTimer = setTimeout(
        () => finish({ status: "cancelled" }),
        CLOSE_GRACE_MS,
      );
    }, CLOSE_POLL_INTERVAL_MS);

    const timeoutTimer = setTimeout(() => {
      if (settled) {
        cleanup();
        return;
      }
      finish({ status: "timeout" });
    }, timeoutMs);

    // Announce only after every timer `cleanup` clears exists, so a broadcast
    // that resolves synchronously (as in tests) cannot hit an uninitialized one.
    const awaitTimer = setInterval(announceAwaiting, AWAIT_INTERVAL_MS);
    announceAwaiting();

    loadAuthorizationUrl().then(
      (authorizationUrl) => {
        if (settled || popup.closed) return;
        try {
          popup.location.replace(authorizationUrl);
        } catch (error) {
          fail(error);
        }
      },
      (error: unknown) => fail(error),
    );
  });
}

/**
 * End-to-end helper for the Graph authorization action: opens the popup, or
 * falls back to a full-page redirect when it is blocked, and maps the popup
 * result to a UI outcome. Never rejects — every path resolves to a typed kind.
 */
export async function authorizeMicrosoftViaPopup(params: {
  readonly apiBase: string;
  readonly connectionId?: string;
  readonly requestAuthorizationUrl: (
    apiBase: string,
    connectionId?: string,
  ) => Promise<string>;
  readonly fullPageNavigate: (url: string) => void;
  readonly win?: Window;
  readonly timeoutMs?: number;
  /**
   * Called when authorization completes after this call already resolved as
   * cancelled — the recovery path for a COOP-severed popup whose handle reported
   * `closed` before the user finished signing in.
   */
  readonly onLateOutcome?: (outcome: MicrosoftAuthorizationOutcome) => void;
}): Promise<MicrosoftAuthorizationOutcome> {
  const win = params.win ?? window;
  const nonce = generateNonce(win);
  const loadUrl = () =>
    params.requestAuthorizationUrl(params.apiBase, params.connectionId);

  const popup = openMicrosoftAuthorizationPopup(win, popupWindowName(nonce));
  if (!popup) {
    try {
      params.fullPageNavigate(await loadUrl());
      return { kind: "redirected" };
    } catch {
      return { kind: "failed" };
    }
  }

  try {
    const result = await runMicrosoftAuthorizationPopup(popup, loadUrl, {
      expectedOrigin: win.location.origin,
      nonce,
      host: win,
      timeoutMs: params.timeoutMs,
      ...(params.onLateOutcome
        ? {
            onLateOutcome: (outcome: OAuthOutcome) =>
              params.onLateOutcome?.(
                outcome.status === "connected"
                  ? { kind: "connected" }
                  : { kind: "failed" },
              ),
          }
        : {}),
    });
    if (result.status === "connected") return { kind: "connected" };
    if (result.status === "cancelled") return { kind: "cancelled" };
    return { kind: "failed" };
  } catch {
    return { kind: "failed" };
  }
}
